import { createSelectorCreator, defaultMemoize } from 'reselect';
import groupBy from 'lodash.groupby';
import isEqual from 'lodash.isequal';

// Global Types
import { Entity } from 'services/entities/types';

// Selectors
import { selectEntities, makeSelectEntityValue } from '../entities/selectors';
// @ts-ignore
import { makeSelectEventValue } from '../events/selectors';

// Types
import { UserEntityAssignment, JobTypeGroupedUserAssignment } from './types';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

interface State {
    userEntityAssignments: UserEntityAssignment[];
}

const selectUserEntityAssignments = (state: State): UserEntityAssignment[] =>
    state.userEntityAssignments;

const selectDataGroupedByEntityId = createDeepEqualSelector(
    selectUserEntityAssignments,
    (userEntityAssignmentsState) =>
        groupBy(userEntityAssignmentsState, 'entityId')
);

const selectEventEntity = createDeepEqualSelector(
    [selectEntities, makeSelectEventValue('entityId')],
    (entitiesState, entityIdState) => entitiesState[entityIdState as number]
);

const makeSelectEventEntityValue = (field: keyof Entity): any =>
    createDeepEqualSelector(
        selectEventEntity,
        (eventEntityState) => eventEntityState && eventEntityState[field]
    );

const selectEventTopEntityId = createDeepEqualSelector(
    [makeSelectEventEntityValue('path'), makeSelectEventEntityValue('id')],
    (eventEntityPathState, eventEntityIdState) => {
        if (!eventEntityPathState) return eventEntityIdState;
        const entityIds = (eventEntityPathState as string)
            .split('/')
            .filter((value) => value);
        return entityIds[0];
    }
);

const selectEntityTopEntityId = createDeepEqualSelector(
    [makeSelectEntityValue('path'), makeSelectEntityValue('id')],
    (entityPathState, entityIdState) => {
        if (!entityPathState) return entityIdState;
        const entityIds = (entityPathState as string)
            .split('/')
            .filter((value) => value);
        return entityIds[0];
    }
);

const selectTopEntityId = createDeepEqualSelector(
    [selectEventTopEntityId, selectEntityTopEntityId],
    (eventTopEntityIdState, entityTopEntityIdState) =>
        eventTopEntityIdState || entityTopEntityIdState
);

const selectEntityData = createDeepEqualSelector(
    [selectDataGroupedByEntityId, selectTopEntityId],
    (dataGroupedByEntityIdState, topEntityIdState) =>
        dataGroupedByEntityIdState[topEntityIdState as number]
);

const selectEntityDefaultJobs = createDeepEqualSelector(
    selectEntityData,
    (entityDataState): JobTypeGroupedUserAssignment[] => {
        return [
            {
                jobType: 'INSPECTION' as UserEntityAssignment['jobType'],
                assignedUsers: entityDataState
                    ? entityDataState
                          .filter((value) => value.jobType === 'INSPECTION')
                          .map((value) => value.email)
                    : []
            },
            {
                jobType: 'TIMESHEET' as UserEntityAssignment['jobType'],
                assignedUsers: entityDataState
                    ? entityDataState
                          .filter((value) => value.jobType === 'TIMESHEET')
                          .map((value) => value.email)
                    : []
            }
        ];
    }
);

export {
    selectUserEntityAssignments,
    selectDataGroupedByEntityId,
    selectEntityData,
    selectEntityDefaultJobs
};
